import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { UpdateAccountGroup } from 'api/Trading-Accounts/Types/UpdateAccountGroup'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchCountriesAction } from 'store/identity/identity.actions'
import {
  fetchAllServerAction,
  fetchBooksTypeAction,
  fetchExtendedTradingAccountTypeAction,
  fetchPlatformTypesAction,
  fetchServerAction,
  fetchTradingAccountClassificationTypeAction,
  fetchTradingAccountGroupTypeAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { accountGroupDetailsRouteUrl, links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import { AccountGroupNavigation, AccountGroupTab } from '../Navigation/AccountGroupNavigation'
import { AccountGroupDetailsForm } from './AccountGroupDetailsForm'

const AccountGroupDetailsPage = (): JSX.Element => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [auth] = useContext(AuthSessionContext)
  const [crumbs, setCrumbs] = useState([
    { title: 'Account Groups', link: links.recordsAccountGroups.url },
  ])

  const { id } = useParams<{ id: string }>()
  const [isEdit, setEdit] = useState<boolean>(false)
  const setToast = useContext(ToastContext)

  const dispatch = useAppDispatch()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? tradingAccountsApiClient.getAccountGroup(id) : Promise.resolve(null)),
    [id]
  )
  const { data, isLoading } = useFetchOne(callback)

  const tradingAccountRestrictions =
    useAppSelector((state) => state.tradingAccounts.restrictions) ?? []
  const tradingAccountType =
    useAppSelector((state) => state.tradingAccounts.extendedTradingAccountType) ?? []
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes) ?? []
  const servers = useAppSelector((state) => state.tradingAccounts.platformServers) ?? []
  const groupTypes = useAppSelector((state) => state.tradingAccounts.tradingAccountGroupType) ?? []
  const bookTypes = useAppSelector((state) => state.tradingAccounts.bookType) ?? []
  const classificationTypes =
    useAppSelector((state) => state.tradingAccounts.tradingAccountClassificationType) ?? []
  const countries = useAppSelector((state) => state.identity.countries) ?? []

  const permissions = auth?.permissions || []
  useEffect(() => {
    if (isEmpty(platformTypes)) dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
    if (isEmpty(servers)) {
      if (data?.platformType?.id) {
        dispatch(fetchServerAction(data?.platformType?.id, tradingAccountsApiClient))
      } else {
        dispatch(fetchAllServerAction(tradingAccountsApiClient))
      }
    }
    if (isEmpty(groupTypes)) dispatch(fetchTradingAccountGroupTypeAction(tradingAccountsApiClient))
    if (isEmpty(bookTypes)) dispatch(fetchBooksTypeAction(tradingAccountsApiClient))
    if (isEmpty(classificationTypes))
      dispatch(fetchTradingAccountClassificationTypeAction(tradingAccountsApiClient))
    if (isEmpty(countries)) dispatch(fetchCountriesAction(identityApiClient))
  }, [id, data?.platformType?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const getAccountTypeOptions = async ({
    platformTypeId,
    accountGroupTypeId,
  }: {
    platformTypeId: number
    accountGroupTypeId: number
  }) => {
    if (!data?.currency.id) return
    const props = { platformTypeId, currencyId: data?.currency.id, accountGroupTypeId }
    dispatch(fetchExtendedTradingAccountTypeAction(tradingAccountsApiClient, props))
  }

  useEffect(() => {
    if (data) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: data.name,
          link: accountGroupDetailsRouteUrl(data),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: UpdateAccountGroup) => {
    id ? await tradingAccountsApiClient.updateAccountGroup(id, values) : Promise.resolve(null)
    setToast(successToast('Account Group updated successfully.'))
    forceRefresh()
  }

  if (isLoading) {
    return <Spinner />
  }

  if (!data) {
    return <PageNotFound />
  }

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <AccountGroupNavigation data={data} isEdit={isEdit} tab={AccountGroupTab.DETAILS} />
      <AccountGroupDetailsForm
        servers={servers}
        data={data}
        isEdit={isEdit}
        setEdit={setEdit}
        onSubmit={onSubmit}
        getAccountTypeOptions={getAccountTypeOptions}
        tradingAccountType={tradingAccountType}
        platformTypes={platformTypes}
        groupTypes={groupTypes}
        bookTypes={bookTypes}
        classificationTypes={classificationTypes}
        tradingAccountRestrictions={tradingAccountRestrictions}
        countries={countries}
        permissions={permissions}
      />
    </React.Fragment>
  )
}

export default AccountGroupDetailsPage
